'use client';

import type { Swiper as SwiperClass } from 'swiper';
import type { BundleOfferViewModel } from '@/queries/bundles/bundleOffers.mapper';
import { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { GmSection } from '@/components/entities/gamification/GmSection';
import { GmBundleOffersSkeleton } from '@/components/entities/gamification/skeletons/GmBundleOffersSkeleton';
import { useMarketFiatCurrency } from '@/hooks/useMarketFiatCurrency';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { BundleCard } from '@/modals/modules/balances/+ShopModal/components/ShopModalContent/components/bundleCards/BundleCard';
import { useBundleGroups } from '@/modals/modules/balances/+ShopModal/components/ShopModalContent/hooks/useBundleGroups';
import { DepositStep } from '@/modals/modules/balances/+ShopModal/types/DepositStep';
import openModal from '@/modals/utils/openModal';
import { mapBundleOfferToBundle } from '@/queries/bundles/bundleOffers.mapper';
import { cn } from '@/utils/cn';
import 'swiper/css';

interface GmBundleOffersProps {
  className?: string;
}

export const GmBundleOffers = ({ className }: GmBundleOffersProps) => {
  const {
    isLoading,
    gamificationShopOffers = [],
  } = useBundleGroups();
  const { currency: fiatCurrency } = useMarketFiatCurrency();
  const [, setDepositStep] = useState<DepositStep>(DepositStep.BUNDLES);
  const [mainSwiper, setMainSwiper] = useState<SwiperClass | null>(null);
  const [activePage, setActivePage] = useState(0);
  const isMobile = useMediaQuery('(max-width: 767px)', { initializeWithValue: false });
  // From xxl (1440px, see --breakpoint-xxl) cards fill 1/3 of the row instead of keeping their default size.
  const isDesktopGrid = useMediaQuery('(min-width: 1280px)', { initializeWithValue: false });
  // Matches .global-container's own gutter so slides bleed to the screen edge, not the section edge.
  const isWideGutter = useMediaQuery('(min-width: 1024px)', { initializeWithValue: false });

  const slidesPerView = isDesktopGrid ? 3 : isMobile ? 1 : 'auto';
  const slidesPerGroup = isDesktopGrid ? 3 : 1;
  const totalPages = isDesktopGrid ? Math.max(1, Math.ceil(gamificationShopOffers.length / 3)) : gamificationShopOffers.length;
  const gutter = isWideGutter ? 40 : 16;

  const handleBundleSelect = (bundle: BundleOfferViewModel) => {
    openModal('deposit', { activeOrder: bundle });
  };

  if (isLoading) {
    return (
      <div className={className}>
        <GmBundleOffersSkeleton />
      </div>
    );
  }

  if (gamificationShopOffers.length === 0) {
    return null;
  }

  return (
    <div className={className}>
      <GmSection
        title="Bundle offers"
        className="**:data-section-title:hidden md:**:data-section-title:block"
      >
        <div className="-mx-4 overflow-x-hidden overflow-y-visible lg:-mx-10">
          <Swiper
            key={slidesPerView}
            onSwiper={setMainSwiper}
            onSlideChange={swiper => setActivePage(Math.floor(swiper.activeIndex / slidesPerGroup))}
            slidesPerView={slidesPerView}
            slidesPerGroup={slidesPerGroup}
            spaceBetween={20}
            slidesOffsetBefore={gutter}
            slidesOffsetAfter={gutter}
            className="overflow-visible! pb-2"
            wrapperClass="pt-4"
          >
            {gamificationShopOffers.map(offer => (
              <SwiperSlide key={offer.id} className={slidesPerView === 'auto' ? 'w-82!' : undefined}>
                <BundleCard
                  variant="gamification"
                  className="w-full"
                  setDepositStep={setDepositStep}
                  handleBundleSelect={handleBundleSelect}
                  bundle={mapBundleOfferToBundle(offer, fiatCurrency)}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
        {totalPages > 1 && (
          <div className="flex justify-center pt-1">
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }).map((_, index) => (
                <button
                  key={index}
                  type="button"
                  aria-label={`Go to bundle page ${index + 1}`}
                  onClick={() => mainSwiper?.slideTo(index * slidesPerGroup)}
                  className={cn(
                    'h-1.25 rounded-full transition-all',
                    activePage === index ? 'w-6 bg-c400' : 'w-1.25 bg-white/30',
                  )}
                />
              ))}
            </div>
          </div>
        )}
      </GmSection>
    </div>
  );
};
